import { z } from "zod";

import fetch from "@lib/fetch";

import { parseItemsLeniently } from "./parseItems";
import type { RemoteLookupProvider } from "./providers";
import type { SearchFetcher, SearchParams, SearchResult } from "./types";

const rawResultSchema = z.object({
  items: z.array(z.unknown()),
  has_next_page: z.boolean(),
});

/**
 * Creates the search fetcher for a provider.
 *
 * Items that fail the provider's item schema are dropped and reported, except
 * that a non-empty page in which every item failed rejects with
 * RemoteLookupInvalidItemsError: a broken provider should show up as a
 * failed search, not an empty dropdown (see parseItems.ts). `has_next_page`
 * is passed through untouched, so dropping items never truncates pagination.
 */
export function createSearchFetcher<T extends string = string, Id = unknown, Item = unknown, Filters = unknown>(
  type: T,
  provider: RemoteLookupProvider<Id, Item, Filters>
): SearchFetcher<Item, Filters> {
  const paramsSchema = z.object({
    app_group_id: z.string(),
    query: z.string(),
    filters: provider.validators.filters,
    page_index: z.number().int().nonnegative(),
  });
  return async (signal: AbortSignal, params: SearchParams<Filters>): Promise<SearchResult<Item>> => {
    const parsedParams = paramsSchema.parse(params);
    const result: unknown = await fetch(
      `/remote_lookup/${parsedParams.app_group_id}/${type}/search`,
      "POST",
      {
        query: parsedParams.query,
        filters: parsedParams.filters,
        page_index: parsedParams.page_index,
      },
      { signal }
    );
    const raw = rawResultSchema.parse(result);
    const parsed = parseItemsLeniently({
      provider,
      providerType: type,
      method: "search",
      rawItems: raw.items,
      failWhenAllInvalid: true,
    });
    return { items: parsed.items.map(({ item }) => item), has_next_page: raw.has_next_page };
  };
}
